import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Folder, Plus, ArrowRight, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    case_id: '',
    client_id: '',
    file_name: '',
    file_url: '',
    file_type: 'pdf',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        client_id: user.role === 'client' ? user.id : formData.client_id,
      };
      await axios.post(`${API}/documents`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchDocuments();
      setFormData({
        title: '',
        case_id: '',
        client_id: '',
        file_name: '',
        file_url: '',
        file_type: 'pdf',
      });
    } catch (err) {
      console.error('Error creating document:', err);
    }
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">المستندات</h1>
          </div>
          <button
            data-testid="add-document-btn"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            إضافة مستند
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover-lift"
              data-testid={`document-card-${doc.id}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-8 h-8 text-gold" />
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-primary mb-2">
                    {doc.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1 font-body">
                    اسم الملف: {doc.file_name}
                  </p>
                  <p className="text-sm text-gray-600 mb-1 font-body">
                    النوع: {doc.file_type.toUpperCase()}
                  </p>
                  <p className="text-sm text-gray-500 font-body">
                    {new Date(doc.created_at).toLocaleDateString('ar-EG')}
                  </p>
                </div>
              </div>
              <a
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-900 transition-all duration-300 font-body"
              >
                فتح المستند
              </a>
            </div>
          ))}
        </div>

        {documents.length === 0 && (
          <div className="text-center py-16" data-testid="no-documents">
            <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد مستندات حاليا</p>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" data-testid="document-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              إضافة مستند جديد
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                data-testid="document-title-input"
                type="text"
                placeholder="عنوان المستند"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <input
                data-testid="document-filename-input"
                type="text"
                placeholder="اسم الملف"
                value={formData.file_name}
                onChange={(e) => setFormData({ ...formData, file_name: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <input
                data-testid="document-url-input"
                type="url"
                placeholder="رابط الملف"
                value={formData.file_url}
                onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="document-type-input"
                  type="text"
                  placeholder="نوع الملف (pdf, docx, etc.)"
                  value={formData.file_type}
                  onChange={(e) => setFormData({ ...formData, file_type: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
                {user?.role === 'lawyer' && (
                  <input
                    data-testid="document-client-id-input"
                    type="text"
                    placeholder="معرف العميل"
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    required
                    className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                )}
              </div>
              <input
                data-testid="document-case-id-input"
                type="text"
                placeholder="رقم القضية (اختياري)"
                value={formData.case_id}
                onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <div className="flex gap-4 justify-end">
                <button
                  data-testid="document-cancel-btn"
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="document-submit-btn"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
